import { motion } from "framer-motion";
import { IMG } from "../lib/config";

const chapters = [
    {
        n: "01",
        title: "Il percorso",
        body: "Dopo anni ad allenare persone di ogni tipo senza mai dire di NO a nessuna esperienza nuova. Ogni palestra, ogni corpo, ogni disciplina e ogni situazione mi hanno insegnato qualcosa.",
    },
    {
        n: "02",
        title: "La scoperta",
        body: "Poi ho incontrato un mondo poco conosciuto: quello dell'allenamento durante la gravidanza e nel post parto. Un territorio che affascina e allo stesso tempo intimorisce, perché troppo spesso non si sa da dove iniziare né come muoversi in sicurezza.",
    },
    {
        n: "03",
        title: "La missione",
        body: "Ho deciso di mettere le mie conoscenze al servizio delle persone: mi dedico alla general population — persone di ogni età e livello, dal principiante a chi vuole tornare in forma o migliorare la performance — e accompagno le mamme durante i nove mesi di gravidanza e nel delicato periodo del post parto. Con metodo, presenza, e soprattutto senza paura.",
    },
];

const qualifiche = [
    "Laurea in Scienze Motorie",
    "Laurea in Psicologia",
    "Strength & Conditioning Coach",
    "Project Invictus — Master Allenamento in Gravidanza",
    "Girls Gone Strong — Pre & Post-Natal Coaching Certification",
];

export default function About() {
    return (
        <section id="about" className="relative bg-powder py-24 md:py-40 overflow-hidden">
            <div className="max-w-[1600px] mx-auto px-5 md:px-12">
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.7 }}
                    className="text-xs uppercase tracking-[0.28em] font-sans text-ink-muted mb-8 md:mb-16"
                >
                    <span className="inline-block w-8 h-px bg-fuchsia align-middle mr-3" />
                    Chi sono
                </motion.p>

                {/* Presentation on top */}
                <motion.h2
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9 }}
                    className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[0.95] font-bold text-ink mb-8 md:mb-16 max-w-5xl"
                >
                    Ciao, sono <span className="italic text-fuchsia">Amanda</span>.<br />
                    Personal Trainer e Strength & Conditioning coach.
                    <span className="block mt-4 text-2xl md:text-4xl lg:text-5xl font-normal text-ink-muted italic">
                        Sono pronta ad aiutarti a raggiungere i tuoi obiettivi.
                    </span>
                </motion.h2>

                <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
                    {/* Portrait */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 1, ease: [0.65, 0, 0.35, 1] }}
                        className="md:col-span-6 relative"
                    >
                        <div className="grid grid-cols-2 gap-4 md:gap-5">
                            <div className="relative overflow-hidden aspect-[3/4] bg-ink">
                                <img
                                    src={IMG.about}
                                    alt="Allenamento fitness"
                                    className="w-full h-full object-cover"
                                />
                                <span className="absolute top-3 left-3 bg-white/90 text-ink text-[9px] uppercase tracking-[0.25em] px-2.5 py-1 rounded-full">
                                    Fitness
                                </span>
                            </div>
                            <div className="relative overflow-hidden aspect-[3/4] bg-ink mt-10 md:mt-16">
                                <img
                                    src={IMG.aboutPregnancy}
                                    alt="Allenamento in gravidanza"
                                    className="w-full h-full object-cover"
                                />
                                <span className="absolute top-3 left-3 bg-fuchsia text-white text-[9px] uppercase tracking-[0.25em] px-2.5 py-1 rounded-full">
                                    FitMama
                                </span>
                            </div>
                        </div>
                        <div className="mt-6 md:mt-8 bg-fuchsia text-white p-5 md:p-7 max-w-full">
                            <p className="font-serif italic text-lg md:text-2xl leading-snug">
                                &ldquo;Non esiste un allenamento perfetto: esiste quello giusto per te, per il momento che stai vivendo. Il mio compito è aiutarti a trovarlo — con la testa, con il corpo e con la voglia di sentirti bene, sempre.&rdquo;
                            </p>
                        </div>
                    </motion.div>

                    {/* Text side */}
                    <div className="md:col-span-6 md:pl-6">
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.15 }}
                            className="font-sans text-base md:text-lg text-ink-muted leading-relaxed max-w-xl mb-12"
                        >
                            Ho iniziato allenando persone di ogni tipo — dagli sportivi amatoriali fino a una squadra di football americano (ebbene sì 😅💪🏻) — e oggi porto quella stessa cura nel costruire percorsi di fitness, forza e ricomposizione per chi vuole sentirsi bene nel proprio corpo, a ogni età e livello.
                            <br /><br />
                            Nel tempo mi sono specializzata anche nell'accompagnare le donne durante gravidanza e post parto, perché credo che non siano una &ldquo;pausa&rdquo; dal proprio corpo, ma un nuovo inizio.
                        </motion.p>

                        <div className="space-y-8 md:space-y-10">
                            {chapters.map((c, i) => (
                                <motion.div
                                    key={c.n}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{ duration: 0.7, delay: i * 0.1 }}
                                    className="grid grid-cols-[auto_1fr] gap-6 md:gap-10 items-start border-t border-ink/10 pt-6 md:pt-8"
                                >
                                    <span className="font-serif italic text-3xl md:text-4xl text-fuchsia">{c.n}</span>
                                    <div>
                                        <h3 className="font-serif text-2xl md:text-3xl mb-2 text-ink">{c.title}</h3>
                                        <p className="font-sans text-ink-muted leading-relaxed max-w-xl">{c.body}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            transition={{ delay: 0.3, duration: 0.8 }}
                            className="mt-12 flex flex-wrap gap-2"
                        >
                            {qualifiche.map((q) => (
                                <span
                                    key={q}
                                    className="border border-ink/20 rounded-full px-4 py-2 text-xs md:text-sm font-sans text-ink"
                                >
                                    {q}
                                </span>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
}
